import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { adminApi } from '@/lib/adminApi';
import { genePhenotype, type CpicCandidate } from '@/lib/cpic';

/**
 * CPIC candidate review: edit the proposed Safety Index entry (JSON), add notes,
 * then Save draft (→ pending_review) or Promote (→ writes the live Safety Index
 * entry + marks promoted). The CPIC recommendation is shown read-only as context.
 */
export function CpicReviewModal({ candidate, onClose }: { candidate: CpicCandidate; onClose: () => void }) {
  const qc = useQueryClient();
  const [json, setJson] = useState(JSON.stringify(candidate.proposed_safety_entry ?? {}, null, 2));
  const [notes, setNotes] = useState(candidate.translation_notes ?? '');
  const [err, setErr] = useState<string | null>(null);

  const parse = (): unknown => { setErr(null); return JSON.parse(json); };
  const done = () => { void qc.invalidateQueries({ queryKey: ['cpic'] }); onClose(); };

  const saveDraft = useMutation({
    mutationFn: () => adminApi.cpic.saveDraft(candidate.candidate_id, { proposed_safety_entry: parse(), translation_notes: notes || undefined }),
    onSuccess: done,
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'Error'),
  });
  const promote = useMutation({
    mutationFn: () => adminApi.cpic.promote(candidate.candidate_id, { proposed_safety_entry: parse(), translation_notes: notes || undefined }),
    onSuccess: done,
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'Error'),
  });

  let jsonValid = true;
  try { JSON.parse(json); } catch { jsonValid = false; }
  const busy = saveDraft.isPending || promote.isPending;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-overlay" onClick={onClose}>
      <div className="w-[640px] max-w-[94vw] rounded bg-surface p-21" onClick={(e) => e.stopPropagation()}>
        <div className="mb-13 flex items-start justify-between gap-8">
          <div>
            <h3 className="text-h3 font-semibold">Review pharmacogenomic candidate</h3>
            <div className="font-mono text-[11px] text-text-muted">{candidate.candidate_id}</div>
          </div>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary">✕</button>
        </div>

        <div className="mb-13 space-y-1 rounded border border-border bg-bg p-13 text-body-sm">
          <div><span className="font-semibold">{candidate.cpic?.drugname || '—'}</span> · {candidate.cpic?.classification || '—'}</div>
          <div className="text-text-secondary">{genePhenotype(candidate)}</div>
          {candidate.cpic?.drugrecommendation ? <div className="text-[12px] text-text-secondary">{candidate.cpic.drugrecommendation}</div> : null}
        </div>

        <label className="mb-1 block text-body-sm font-medium">Proposed Safety Index entry (JSON)</label>
        <textarea
          value={json} onChange={(e) => setJson(e.target.value)} spellCheck={false}
          className="min-h-[240px] w-full resize-y rounded border border-border-strong bg-bg px-13 py-8 font-mono text-[12px] leading-snug focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
        />
        {!jsonValid ? <div className="mt-1 text-[11px] text-flagged">Invalid JSON</div> : null}

        <label className="mb-1 mt-13 block text-body-sm font-medium">Notes</label>
        <textarea
          value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Reviewer notes (optional)…"
          className="min-h-[52px] w-full resize-y rounded border border-border-strong bg-bg px-13 py-8 text-body-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
        />

        {err ? <p className="mt-8 text-body-sm text-flagged">{err}</p> : null}
        <div className="mt-13 flex items-center justify-end gap-8">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="outline" size="sm" disabled={!jsonValid || busy} onClick={() => saveDraft.mutate()}>{saveDraft.isPending ? 'Saving…' : 'Save draft'}</Button>
          <Button size="sm" disabled={!jsonValid || busy} onClick={() => promote.mutate()}>{promote.isPending ? 'Promoting…' : 'Promote → Safety Index'}</Button>
        </div>
      </div>
    </div>
  );
}
